import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';

const DataRangeContext = createContext();

export const useDataRange = () => {
  const context = useContext(DataRangeContext);
  if (!context) throw new Error('useDataRange must be used within DataRangeProvider');
  return context;
};

const LS_KEY_THROUGH = 'dataRange:dataThrough';
const LS_KEY_FROM = 'dataRange:dataFrom';

export const DataRangeProvider = ({ children }) => {
  const [dataThrough, setDataThrough] = useState(() => localStorage.getItem(LS_KEY_THROUGH) || null);
  const [dataFrom, setDataFrom] = useState(() => localStorage.getItem(LS_KEY_FROM) || null);
  const [dataRefreshedAt, setDataRefreshedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchDataRange = async (attempt = 0) => {
      try {
        const headers = {};
        const token = localStorage.getItem('authToken');
        if (token) headers.Authorization = `Bearer ${token}`;
        const resp = await axios.get('/api/ai-command/sync-status', { headers, timeout: 8000 });
        if (cancelled) return;
        const through = resp.data?.data_through || null;
        const from = resp.data?.data_from || null;
        const refreshedAt = resp.data?.data_refreshed_at || null;
        setDataThrough(through);
        setDataFrom(from);
        setDataRefreshedAt(refreshedAt);
        if (through) localStorage.setItem(LS_KEY_THROUGH, through);
        if (from) localStorage.setItem(LS_KEY_FROM, from);
      } catch (err) {
        console.warn(`[DataRangeContext] sync-status fetch failed (attempt ${attempt + 1}):`, err.message);
        if (!cancelled && attempt < 2) {
          setTimeout(() => fetchDataRange(attempt + 1), 2000);
          return;
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const scheduleSync = () => { if (!cancelled) fetchDataRange(); };
    const id = typeof requestIdleCallback !== 'undefined'
      ? requestIdleCallback(scheduleSync)
      : setTimeout(scheduleSync, 2000);

    return () => {
      cancelled = true;
      if (typeof requestIdleCallback !== 'undefined') {
        cancelIdleCallback(id);
      } else {
        clearTimeout(id);
      }
    };
  }, []);

  const latestDataMonth = useMemo(() => {
    if (dataThrough) return dayjs(dataThrough).startOf('month');
    return dayjs().startOf('month');
  }, [dataThrough]);

  return (
    <DataRangeContext.Provider value={{ latestDataMonth, dataThrough, dataFrom, dataRefreshedAt, loading }}>
      {children}
    </DataRangeContext.Provider>
  );
};
